/**
 * CA 在线申请 · 客户域名识别
 *
 * 约定：客户域名为「{code}.{平台主域}」，首段是客户专区编码（code）。
 * 采用「专区编码白名单」匹配：只有首段命中 CA_CUSTOMER_CODES 才识别为客户门户——
 * 无需维护平台主域清单，运维新增环境/动态专区前缀（如 hlj.testjfv2.ebidding.net.cn）
 * 天然不会被接管；新增客户只需在 CA_CUSTOMER_CODES 追加其专区编码。
 * 解析不出返回 ''（正常值，非错误）——路由守卫改用 URL ?code / store / 开发默认编码兜底，
 * 本地开发（localhost / IP）恒为 ''，不受影响。
 */

/**
 * 已部署的客户专区编码集合（唯一需要维护的配置）。
 * 白名单外的 host 一律不接管，主站/动态专区/未知域名天然安全。
 *
 * 所有子站编码：zcjbca、bsgzwca、rbecca、zgtpca、gzfztca、gzfzcca、
 *              tgzcca、zgxdca、ceczhca、gsjcca、qdgxca、dkzcca。
 * 运维新增客户时在此追加。
 * 首项 zcjbca 同时是本地开发默认编码（resolveDevCustomerCode 取数组首项），调整顺序前先确认。
 */
export const CA_CUSTOMER_CODES = [
  'zcjbca',
  'bsgzwca',
  'rbecca',
  'zgtpca',
  'gzfztca',
  'gzfzcca',
  'tgzcca',
  'zgxdca',
  'ceczhca',
  'gsjcca',
  'qdgxca',
  'dkzcca',
]

/**
 * host 归一化：去空白 → 小写 → 去尾点 → 去端口 → 去 www. 前缀。
 * ⚠️ 不做 includes 包含式匹配（会把 localhost 误判成客户域名、毁掉本地联调）。
 */
export function normalizeHost(host) {
  if (typeof host !== 'string') return ''
  let h = host.trim().toLowerCase().replace(/\.$/, '')
  h = h.replace(/:\d+$/, '') // 只削尾部 :数字，不影响 IPv6 中括号内的冒号
  if (h.indexOf('www.') === 0) h = h.slice(4)
  return h
}

/**
 * 由 host 解析 customerCode（主入口，业务调用只传 host）。
 * @returns {string} customerCode；解析不出返回 ''（调用方改用 URL ?code，不是错误）
 */
export function resolveCustomerCodeByHost(host) {
  const h = normalizeHost(host)
  if (!h || h.indexOf('.') === -1) return '' // localhost 等单段 host
  if (/^\d+(\.\d+)+$/.test(h)) return '' // IP 地址
  const first = h.split('.')[0]
  return CA_CUSTOMER_CODES.indexOf(first) > -1 ? first : ''
}

/** host 是否可识别为「客户门户域名」—— 决定路由守卫是否执行整站接管 */
export function isCaCustomerHost(host) {
  return !!resolveCustomerCodeByHost(host)
}

/**
 * host 是否为本地开发宿主：localhost / 127.x / ::1 / 私网 IPv4（10.x、172.16-31.x、192.168.x）。
 * 只用于「开发默认客户编码」兜底，绝不影响整站接管判断（接管只看 resolveCustomerCodeByHost）。
 */
export function isLocalDevHost(host) {
  const h = normalizeHost(host)
  if (!h) return false
  if (h === 'localhost' || h === '::1' || h === '[::1]') return true
  if (!/^\d+(\.\d+){3}$/.test(h)) return false // 非 IPv4 一律不算
  const parts = h.split('.')
  const a = +parts[0]
  const b = +parts[1]
  return a === 127 || a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168)
}

/**
 * 本地开发兜底客户编码：host 是本地开发宿主时返回白名单首个编码（当前 zcjbca），否则 ''。
 * 场景：localhost / 局域网 IP（如 http://192.168.1.2:8092）访问 /ca 时不带 ?code、
 * 且 localStorage（按源隔离，IP 与 localhost 不互通）里也没有缓存编码 ——
 * 没有这层兜底就会跳 ca-error。生产域名 hostCode 优先于它，永远不会走到这里。
 * 要调试其他客户仍可用 ?code=zgxdca 显式覆盖。
 */
export function resolveDevCustomerCode(host) {
  return isLocalDevHost(host) ? CA_CUSTOMER_CODES[0] : ''
}
